/*
Ticker: live news from the radio. Mirrors the radio site's selection rules —
breaking segments from the past 12h plus the freshest quick hits and takes —
but renders them in the news site's own style: plain scrolling text, no
buttons. Speed scales with content (~2.6 chars/sec, 20s floor).
*/
import afterLoad from './afterLoad';

const AIRED_URL = 'https://radio.thehype.news/aired-history';
const WINDOW_HOURS = 12;
const POLL_MS = 5 * 60 * 1000;

// Segment types the ticker shows and how many of each; the type itself is not printed.
const TYPE_LIMITS = { breaking: Infinity, quick_hit: 3, take: 2 };

function pickItems(list) {
    const cutoff = Date.now() - WINDOW_HOURS * 3600 * 1000;
    const seenIds = new Set();
    const perType = {};
    return list
        .filter(s => s && s.type in TYPE_LIMITS && s.title)
        .filter(s => {
            const t = Date.parse(s.aired_at || '');
            return !Number.isNaN(t) && t >= cutoff;
        })
        .sort((a, b) => Date.parse(b.aired_at) - Date.parse(a.aired_at))
        .filter(s => {
            if (seenIds.has(s.segment_id)) return false;
            seenIds.add(s.segment_id);
            perType[s.type] = (perType[s.type] || 0) + 1;
            return perType[s.type] <= TYPE_LIMITS[s.type];
        })
        .reverse(); // back to chronological order for display
}

function buildItem(seg, hidden) {
    const item = document.createElement('span');
    item.className = 'th-ticker-item';
    if (hidden) {
        item.setAttribute('aria-hidden', 'true');
    }

    const time = document.createElement('span');
    time.className = 'th-ticker-time';
    const d = new Date(seg.aired_at);
    time.textContent = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');

    const title = document.createElement('span');
    title.className = 'th-ticker-title';
    title.textContent = seg.title;

    item.append(time, title);
    return item;
}

let _renderedIds = '';
let _polling = false;

async function refresh() {
    // Re-queried every time: PJAX navigation replaces the ticker element.
    const root = document.querySelector('.th-ticker');
    const track = root?.querySelector('.th-ticker-track');
    if (!root || !track || document.hidden) return;

    let items = [];
    try {
        const r = await fetch(AIRED_URL, { cache: 'no-store' });
        if (!r.ok) return;
        const data = await r.json();
        items = pickItems(Array.isArray(data) ? data : data.items || []);
    } catch (_) {
        return; // keep whatever is already on screen
    }

    // No news: keep the reserved strip (invisible) instead of collapsing it —
    // collapsing after first paint would shift the whole page up by 45px.
    if (!items.length) {
        root.dataset.state = 'empty';
        track.textContent = '';
        _renderedIds = '';
        return;
    }

    const ids = items.map(s => s.segment_id).join('|');
    if (ids === _renderedIds && track.childElementCount > 0) return;
    _renderedIds = ids;

    track.textContent = '';
    // The list is rendered twice so translateX(-50%) loops seamlessly.
    items.forEach(s => track.appendChild(buildItem(s, false)));
    items.forEach(s => track.appendChild(buildItem(s, true)));

    const chars = items.reduce((n, s) => n + s.title.length, 0);
    track.style.setProperty('--ticker-duration', Math.max(20, chars * 0.38) + 's');
    delete root.dataset.state;
}

export default function ticker() {
    afterLoad(startTicker);
}

// The first fetch and the polling both wait for the page load, so the feed
// never competes with the hero image.
function startTicker() {
    refresh();
    if (!_polling) {
        _polling = true;
        setInterval(refresh, POLL_MS);
    }
}
